import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Typography,
    CircularProgress
} from '@mui/material';

interface RenameDocumentDialogProps {
    open: boolean;
    onClose: () => void;
    onRename: (newName: string) => void;
    originalName: string;
    existingNames: string[];
    subCategories: string[];
}

export const RenameDocumentDialog: React.FC<RenameDocumentDialogProps> = ({
    open,
    onClose,
    onRename,
    originalName,
    existingNames,
    subCategories
}) => {
    const [newName, setNewName] = useState(originalName);
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (open) {
            setNewName(originalName);
            setError(null);
            setIsSubmitting(false);
        }
    }, [open, originalName]);

    const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setNewName(value);
        
        if (!value.trim()) {
            setError('Document name cannot be empty');
        } else if (existingNames.includes(value)) {
            setError('A document with this name already exists');
        } else if (subCategories.includes(value)) {
            setError('Document contains a sub category with this name');
        } else {
            setError(null);
        }
    };

    const handleConfirm = async () => {
        if (!error && newName.trim()) {
            setIsSubmitting(true);
            onRename(newName.trim());
            onClose();
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Rename Document</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    margin="dense"
                    label="Document Name"
                    type="text"
                    fullWidth
                    value={newName}
                    onChange={handleNameChange}
                    error={!!error}
                    helperText={error}
                    disabled={isSubmitting}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isSubmitting}>Cancel</Button>
                <Button 
                    onClick={handleConfirm} 
                    color="primary" 
                    disabled={!!error || !newName.trim() || isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                >
                    {isSubmitting ? 'Loading...' : 'Confirm'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}; 